import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ChatService } from '../../services/chat-service/chat.service';
import { BroadcasterEvent } from '../../models/broadcaster-events.enum';
import { ChatBroadcaster } from 'src/app/services/chat-broadcaster/chat-broadcaster';
import { UserService } from 'src/app/services/user-service/user.service';
import { environment } from 'src/environments/environment';
import { ChatMsgType } from 'src/app/models/chat-types.enum';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Subscription, timer } from 'rxjs';
import { map, tap, delay, catchError, exhaustMap, timeout } from 'rxjs/operators';
import { TranslatableError } from '@cordic/request';
import { Conversation, DriverDetails, OperSearchDriversResponse, SystemVerbs } from '@cordic/chatmail-web';
import { ChatID } from 'src/app/models/chat-id.enum';
import { ChatCount } from 'src/app/models/chat-count.enum';
import * as moment from 'moment';
import { ChatMessagesWithDate } from 'src/app/models/chat-messages';

@Component({
  selector: 'app-contacts-list',
  templateUrl: './contacts-list.component.html',
  styleUrls: ['./contacts-list.component.scss']
})
export class ContactsListComponent implements OnInit {

  recentContactsList: Conversation[] = [];
  resultList: DriverDetails[] = [];

  showResultList = false;
  showNoResultsFoundMsg = false;

  searchTimer;
  searchString: string = '';
  driverSearchSubscription: Subscription;
  selectedContact: DriverDetails | Conversation;


  constructor(private chatService: ChatService,
              private userService: UserService,
              private broadcaster: ChatBroadcaster,
              private translateService: TranslateService) { }

  ngOnInit()
  {
    this.recentContactsList = this.chatService.getChatsFilteredByDespGroup();

    this.startCheckNewMessagesSubscription();

    this.broadcaster.on(BroadcasterEvent.EVENT_ON_MESSAGE_SENT_OR_RETRIEVED)
    .subscribe((data: {message: ChatMessagesWithDate, toDriver: Conversation | DriverDetails}) =>
    {
      if ((!data.message) ||
          (!data.toDriver))
      {
        return;
      }

      let driverID = data.toDriver instanceof DriverDetails ? data.toDriver.id : data.toDriver.driverID;
      let driverName = data.toDriver instanceof DriverDetails ? data.toDriver.username : data.toDriver.driverName;
      let recentChats: Conversation[] = this.chatService.getChatsFilteredByDespGroup();
      let driverRecentChatsIndex = recentChats.findIndex(chats => chats.driverID == driverID);
      
      if (data.message)
      {
        let newChat = new Conversation();
        newChat.driverID = driverID;
        newChat.driverName = driverName;
        newChat.maxChatID = data.message.chatID;
        newChat.newMessage = 0;
        newChat.newResponse = 0;
        newChat.lastMessage = data.message.response ? data.message.response : data.message.text;
        newChat.despatchGroupID = data.toDriver.despatchGroupID;
        newChat.messageType = data.message.type;
        newChat.time = data.message.time;
        if (recentChats &&
            driverRecentChatsIndex >= 0)
        {
          recentChats[driverRecentChatsIndex] = newChat;
        }
        else
        {
          recentChats.push(newChat);
        }

        this.chatService.setRecentChats(recentChats);
        this.populateRecentContactsList(recentChats);
      }
    });
  }
  
  startCheckNewMessagesSubscription()
  {
    this.checkNewMessages()
    .subscribe((response: Conversation[]) =>
    {
      this.populateRecentContactsList(response);
    },
    (error: TranslatableError) =>
    {
      this.chatService.handleError(error);
    });
  }

  populateRecentContactsList(list: Conversation[])
  {
    if (!list) { return; }

    // Show latest chats on the top of the list
    this.recentContactsList = list.sort((a, b) => this.getMsgUTC(b.time).unix() - this.getMsgUTC(a.time).unix());
  }

  populateResultList(list: DriverDetails[])
  {
    this.resultList = [];

    if (list.length === 0)
    {
      this.showNoResultsFoundMsg = true;
    }
    else
    {
      for (let element of list)
      {
        this.resultList.push(element);
      }
      this.showResultList = true;
      this.showNoResultsFoundMsg = false;
    }
  }

  private clearResultsList()
  {
    this.resultList = [];
    this.showResultList = false;
    this.showNoResultsFoundMsg = false;
  }

  public onSearchFieldChanged()
  {
    if (!this.searchString.length)
    {
      this.clearResultsList();
      return;
    }

    clearTimeout(this.searchTimer);
    this.cancelDriverSearch();
    this.searchTimer = setTimeout(() => { this.submitDriverSearch(); }, 500);
  }

  private cancelDriverSearch()
  {
    if (this.driverSearchSubscription)
    {
      this.driverSearchSubscription.unsubscribe();
    }
  }

  public searchFieldKeyPressFunction(event)
  {
    // User presses the Enter key on keyboard
    if ((event.keyCode) &&
        (event.keyCode === 13))
    {
      this.submitDriverSearch();
    }
    else
    {
      return;
    }
  }

  private submitDriverSearch()
  {
    if (!this.searchString.length)
    {
      this.clearResultsList();
      return;
    }

    this.driverSearchSubscription = this.chatService.chatMailService.operSearchDrivers(this.userService.getJWT(),
                                                                                       ChatCount.MAX_CONTACTS,
                                                                                       this.searchString,
                                                                                       this.chatService.getDespatchGroups())
    .subscribe((response: OperSearchDriversResponse) =>
    {
      this.populateResultList(response.drivers);
    },
    (error: TranslatableError) =>
    {
      this.chatService.handleError(error);
    });
  }

  public shouldSelectItem(contact: DriverDetails | Conversation)
  {
    return contact == this.selectedContact;
  }

  public onContactSelected(contact: DriverDetails | Conversation)
  {
    this.clearChatNotifications(contact);
    this.selectedContact = contact;
    this.broadcaster.broadcast(BroadcasterEvent.EVENT_ON_CONTACT_SELECTED, contact);
  }

  private clearChatNotifications(contact: DriverDetails | Conversation)
  {
    if (!contact)
    {
      return;
    }
    
    this.recentContactsList.map(chat =>
    {
      const driverID = contact instanceof DriverDetails ? contact.id : contact.driverID;

      if (chat.driverID == driverID)
      {
        chat.newMessage = 0;
        chat.newResponse = 0;
      }
    });

    this.chatService.setRecentChats(this.recentContactsList);
  }

  checkNewMessages()
  {
    return timer(0, environment.messagePollingInterval)
    .pipe(
      map(() => this.chatService.getChatsFilteredByDespGroup()),
      exhaustMap((recentChats) => {
        return this.chatService.chatMailService.operatorCheck(this.userService.getJWT(),
                                                              ChatID.UNKNOWN_CHAT_ID, this.chatService.getDespatchGroups(),
                                                              null, null, null,
                                                              this.chatService.getOverrideUrl(SystemVerbs.SystemVerbOperatorCheck))
        .pipe(
          map(serverMessages =>
          {
            let conversations = serverMessages.conversations;

            if ((!recentChats) ||
                (recentChats.length === 0))
            {
              return conversations;
            }
            else
            {
              for (let conversation of conversations)
              {
                if (recentChats.some(x => x.driverID === conversation.driverID))
                {
                  recentChats.forEach((element, index) => {
                    if (element.driverID === conversation.driverID)
                    {
                      recentChats.splice(index, 1, conversation);
                    }
                  });
                }
                else
                {
                  recentChats.push(conversation);
                }
              }

              return recentChats;
            }
          }),
          map(combinedChats => combinedChats.sort((a, b) => moment(b.time).unix() - moment(a.time).unix())),
          // if the server takes > 30 seconds to get back with a response, we cancel the observable and subscribe to a new one
          // this will prevent the server to get overload with requests that might take longer than 5 seconds to respond
          timeout(30000),
          tap((chats) =>
          {
            this.chatService.setRecentChats(
                !!chats ? chats.slice(0, environment.maxCacheMessageCount) : chats
            );
          }),
          catchError(error =>
          {
            return EMPTY;
          }),
        );
      })
    );
  }

  shouldShowResultList()
  {
    return this.showResultList;
  }

  shouldShowNoResultsFoundMsg()
  {
    return this.showNoResultsFoundMsg;
  }

  getNewNotifications(contact: Conversation)
  {
    let sumNewMsgs = contact.newMessage + contact.newResponse;

    if ((contact) &&
        (sumNewMsgs > 0))
    {
      return sumNewMsgs;
    }

    return null;
  }

  getLastMessageAuthor(msg: Conversation)
  {
    if ((msg.messageType === ChatMsgType.fromDriver) ||
        (msg.messageType === ChatMsgType.withResponse))
    {
      return null;
    }

    return this.translateService.instant('IDS_YOU');
  }

  getOperatorName()
  {
    return this.userService.getOperatorName();
  }

  getMsgUTC(time: string)
  {
    let timeUTC = moment.utc(time).toDate();
    let timeLocal = moment(timeUTC).local();

    return timeLocal;
  }
  getTimeLastMessage(lastMessageTime: string)
  {
    if (!lastMessageTime)
    {
      return;
    }

    let timeLastMsgLocal = this.getMsgUTC(lastMessageTime);

    // Is last message's time today?
    if ((timeLastMsgLocal.year() === moment().year() &&
        timeLastMsgLocal.month() === moment().month() &&
        timeLastMsgLocal.date() === moment().date()))
    {
      return timeLastMsgLocal.format('HH:mm');
    }

    return timeLastMsgLocal.format('DD/MM');
  }
}
